<!--
  Admin Google Place linker.

  Finds a Google Place via the CloudFront-proxied Places API (GooglePlacesService),
  the same path used by AddRestaurantDialog.vue. The legacy GMapAutocomplete /
  vue-google-maps SDK was removed app-wide (commit 11312a2a); this component was
  migrated to the proxy so search works without the client-side Maps SDK.

  Flow: type name (+ city) -> pick a match -> review -> Link (links AND saves,
  with a server-side dedup guard in restaurantsStore.linkRestaurantToGooglePlace).
-->

<script setup>
import { displayPhone } from '@/plugins/misc'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRestaurantsStore, useUsersStore } from '@/stores'
import GooglePlacesService from '@/services/GooglePlacesService'

const props = defineProps({
  restaurantId: {
    type: String,
    required: true,
  },

  // Pre-fill the search from the restaurant's existing profile so the admin
  // doesn't have to re-type the name/city — and candidates for THIS restaurant
  // surface immediately when the panel opens.
  defaultName: {
    type: String,
    default: '',
  },
  defaultCity: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['confirm', 'cancel'])

const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

const restaurantsStore = useRestaurantsStore()
let restaurants = null;
({ restaurants } = storeToRefs(restaurantsStore))

const service = new GooglePlacesService()

const nameQuery = ref(props.defaultName || '')
const cityQuery = ref(props.defaultCity || '')
const predictions = ref([])
const isSearching = ref(false)
const isLinking = ref(false)
const isLoadingDetails = ref(false)
const hasSearched = ref(false)
const searchError = ref('')

const selectedPlace = ref(null)
const linkError = ref('')

let debounceTimer = null

const runSearch = async () => {
  searchError.value = ''
  const name = nameQuery.value.trim()
  if (name.length < 2) {
    predictions.value = []
    hasSearched.value = false

    return
  }

  const query = `${name} ${cityQuery.value.trim()}`.trim()

  isSearching.value = true
  try {
    const results = await service.getAutocompletePredictions(query, {
      types: ['restaurant', 'bar', 'cafe', 'food'],
    })

    // Enrich each prediction with details so we can show a photo thumbnail +
    // full address, mirroring the Add-favorite selector.
    const detailed = await Promise.all(
      results.slice(0, 6).map(async prediction => {
        try {
          const d = await service.getPlaceDetails(prediction.place_id, [
            'place_id',
            'name',
            'photos',
            'formatted_address',
            'vicinity',
          ])

          return {
            place_id: prediction.place_id,
            name: d.name || prediction.structured_formatting?.main_text || prediction.description,
            address: d.formatted_address || d.vicinity || prediction.structured_formatting?.secondary_text || '',
            photoUrl: d.photos?.[0] ? service.getPhotoUrl(d.photos[0].photo_reference, 100, 100) : null,
          }
        } catch (err) {
          console.error('Error getting place details:', err)

          return {
            place_id: prediction.place_id,
            name: prediction.structured_formatting?.main_text || prediction.description,
            address: prediction.structured_formatting?.secondary_text || '',
            photoUrl: null,
          }
        }
      }),
    )

    predictions.value = detailed
    hasSearched.value = true
  } catch (e) {
    console.error('Place search failed', e)
    searchError.value = 'Search failed. Please try again.'
    predictions.value = []
  } finally {
    isSearching.value = false
  }
}

const triggerSearch = () => {
  if (debounceTimer) clearTimeout(debounceTimer)

  // Editing the query again abandons any previously selected place.
  if (selectedPlace.value) selectedPlace.value = null
  debounceTimer = setTimeout(runSearch, 300)
}

watch([nameQuery, cityQuery], triggerSearch)

onMounted(() => {
  // Auto-search using the restaurant's profile name + city on open.
  if (nameQuery.value.trim().length >= 2) runSearch()
})

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

const selectPrediction = async prediction => {
  linkError.value = ''
  searchError.value = ''
  predictions.value = []
  isLoadingDetails.value = true
  try {
    const details = await service.getPlaceDetails(prediction.place_id, [
      'place_id',
      'name',
      'photos',
      'formatted_address',
      'vicinity',
      'geometry',
      'website',
      'formatted_phone_number',
      'opening_hours',
      'address_components',
      'url',
    ])

    // linkRestaurantToGooglePlace requires place.timeZone.timeZoneId.
    const loc = details.geometry?.location
    if (loc) {
      const tz = await service.getTimeZone(loc.lat, loc.lng)
      details.timeZone = tz || { timeZoneId: 'America/Chicago' }
    } else {
      details.timeZone = { timeZoneId: 'America/Chicago' }
    }

    selectedPlace.value = details
  } catch (e) {
    console.error('Failed to load place details', e)
    searchError.value = 'Could not load details for that place. Please try another.'
  } finally {
    isLoadingDetails.value = false
  }
}

// "Back" from the selected-place card: drop the selection and re-show results
// for the current name/city.
const backToResults = () => {
  selectedPlace.value = null
  linkError.value = ''
  runSearch()
}

// Full reset after a successful link.
const clearSelection = () => {
  selectedPlace.value = null
  predictions.value = []
  linkError.value = ''
  searchError.value = ''
  hasSearched.value = false
}

const restaurantLinkedToSelectedPlace = computed(() => {
  if (!selectedPlace.value) return null

  return (restaurants.value || []).find(r => r.googlePlaceId === selectedPlace.value.place_id) || null
})

const thisRestaurantLinkedToSelectedPlace = computed(() => {
  if (!selectedPlace.value) return null

  return (restaurants.value || []).find(
    r => r.googlePlaceId === selectedPlace.value.place_id && r._id === props.restaurantId,
  ) || null
})

// RM-157: link AND save in one click; surface the dedup/save error if blocked.
const doLink = async () => {
  if (!selectedPlace.value) return
  linkError.value = ''
  isLinking.value = true
  try {
    const result = await restaurantsStore.linkRestaurantToGooglePlace(props.restaurantId, selectedPlace.value)
    if (result?.error) {
      linkError.value = result.error
    } else {
      emit('confirm')
      clearSelection()
    }
  } finally {
    isLinking.value = false
  }
}
</script>

<template>
  <div>
    <!-- Search — prefilled from the restaurant's profile -->
    <VTextField
      v-model="nameQuery"
      label="Restaurant Name"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      autocomplete="off"
      class="mb-3"
    />
    <VTextField
      v-model="cityQuery"
      label="City, State"
      placeholder="e.g. Chicago, IL"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      autocomplete="off"
      :loading="isSearching"
    />

    <!-- Results -->
    <VList
      v-if="predictions.length && !selectedPlace"
      class="mt-2"
      lines="two"
      density="comfortable"
    >
      <VListItem
        v-for="p in predictions"
        :key="p.place_id"
        :title="p.name"
        :subtitle="p.address"
        @click="selectPrediction(p)"
      >
        <template #prepend>
          <VAvatar
            rounded="lg"
            size="48"
            color="grey-lighten-3"
          >
            <img
              v-if="p.photoUrl"
              :src="p.photoUrl"
              :alt="p.name"
              style="width: 100%; height: 100%; object-fit: cover;"
              @error="p.photoUrl = null"
            >
            <VIcon
              v-else
              icon="mdi-silverware-fork-knife"
            />
          </VAvatar>
        </template>
        <template #append>
          <VIcon
            icon="mdi-chevron-right"
            size="18"
          />
        </template>
      </VListItem>
    </VList>

    <!-- States -->
    <div
      v-if="searchError"
      class="text-error text-caption mt-2"
    >
      {{ searchError }}
    </div>
    <div
      v-else-if="hasSearched && !isSearching && !predictions.length && !selectedPlace"
      class="text-medium-emphasis text-caption mt-2"
    >
      No matching places found. Try adding the city.
    </div>

    <div
      v-if="isLoadingDetails"
      class="d-flex align-center mt-3 text-medium-emphasis text-caption"
    >
      <VProgressCircular
        indeterminate
        size="18"
        width="2"
        class="mr-2"
      />
      Loading details…
    </div>

    <!-- Selected place -->
    <VCard
      v-if="selectedPlace"
      class="mx-auto mt-4"
      variant="outlined"
      max-width="800"
    >
      <VCarousel
        v-if="selectedPlace.photos?.length"
        height="180px"
        hide-delimiters
        show-arrows="hover"
      >
        <VCarouselItem
          v-for="(photo, index) in selectedPlace.photos"
          :key="index"
          :src="photo.getUrl()"
          cover
        />
      </VCarousel>

      <VCardTitle>{{ selectedPlace.name }}</VCardTitle>

      <VCardSubtitle>
        {{ selectedPlace.vicinity || selectedPlace.formatted_address }}<br>
        {{ displayPhone(selectedPlace.formatted_phone_number) }}
      </VCardSubtitle>

      <VCardSubtitle v-if="restaurantLinkedToSelectedPlace && !thisRestaurantLinkedToSelectedPlace">
        Currently {{ restaurantLinkedToSelectedPlace.status }} on RM
      </VCardSubtitle>

      <VAlert
        v-if="linkError"
        type="error"
        class="ma-4"
        density="compact"
      >
        {{ linkError }}
      </VAlert>

      <VDivider class="ma-4" />

      <VCardActions>
        <template v-if="thisRestaurantLinkedToSelectedPlace">
          <VSpacer />
          <span class="text-success text-body-2">Already linked to this restaurant ✓</span>
          <VSpacer />
        </template>

        <template v-else-if="restaurantLinkedToSelectedPlace">
          <VAlert
            type="info"
            density="compact"
            class="ma-2"
          >
            This Google Place is already linked to "{{ restaurantLinkedToSelectedPlace.name }}".
          </VAlert>
        </template>

        <template v-else>
          <VBtn
            variant="text"
            :disabled="isLinking"
            @click="backToResults"
          >
            Back
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            :loading="isLinking"
            :disabled="myUser?.userLevel !== 'admin'"
            @click="doLink"
          >
            Link this place
          </VBtn>
        </template>
      </VCardActions>
    </VCard>
  </div>
</template>

<style scoped>
.dialog-close-btn {
  position: fixed;
  right: 6px;
  top: 6px;
}
</style>
